// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  renameFileAction,
  renameFolderAction,
  resetRenameStatusAction,
  drawerInfo,
} from "./drawerInfoSlice";
import { directory, Items } from "../../../directory/directorySlice";
// MUI Components:
import {
  Box,
  Typography,
  TextField,
  Fade,
  Button,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// SVG Icones:
import { ReactComponent as FolderHomeIcon } from "../../../../../../assets/icons/folder-home-icon.svg";
import { drawer } from "../../drawerSlice";

const useStyles = makeStyles((theme) => ({
  collapseBox: {
    marginBottom: "6px",
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
        borderRadius: "5px",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
  multilineField: {
    "& .css-ddljy4-MuiInputBase-root-MuiFilledInput-root": {
      borderRadius: "5px",
      backgroundColor: "#F2F2F2",
      padding: "8px",
      color: theme.palette.text.secondary,
      "&::placeholder": {
        fontSize: "14px",
        color: theme.palette.text.secondary,
      },
      "&:hover": {
        backgroundColor: "#F2F2F2",
      },
    },
  },
  images: {
    width: "auto",
    height: "85px",
    objectFit: "cover",
    color: theme.palette.secondary.light,
    [theme.breakpoints.down("xl")]: {
      height: "75px",
    },
    [theme.breakpoints.down("md")]: {
      height: "65px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "80px",
    },
  },
  addressBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "4px",
      height: "4px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

const byteToMegabyte = (e: number) => {
  const result = e / (1024 * 1024);
  return result.toFixed(2);
};

const DrawerInfo = ({ itemInfo }: any) => {
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { renameData } = useAppSelector(drawerInfo);
  const { directoryData, searchData, searchInput } = useAppSelector(directory);

  const [items, setItems] = useState<Array<Items>>(directoryData.items);

  const [inputs, setInputs] = useState({
    name: "",
    type: "",
    changed: false,
  });
  const [openButton, setOpenButton] = useState(false);

  const { openDrawer } = useAppSelector(drawer);

  useEffect(() => {
    if (searchInput !== "") {
      setItems(searchData.items);
    } else if (!searchData.items.length) {
      setItems(directoryData.items);
    }
  }, [directoryData.items, searchData.items, searchInput]);

  useEffect(() => {
    setInputs({
      name: itemInfo.dir
        ? itemInfo.name
        : itemInfo.type
        ? itemInfo.name
            .split("")
            .reverse()
            .slice(
              itemInfo.name
                .split("")
                .reverse()
                .findIndex((item: string) => item === ".") + 1,
              itemInfo.name.split("").reverse().length
            )
            .reverse()
            .join("")
        : itemInfo.name,
      type: itemInfo.type ? itemInfo.type : "",
      changed: false,
    });
  }, [itemInfo.dir, itemInfo.name, itemInfo.type]);

  useEffect(() => {
    if (!!inputs.name && inputs.changed === true) {
      setOpenButton(true);
    } else {
      setOpenButton(false);
    }
  }, [inputs.changed, inputs.name]);

  useEffect(() => {
    if (
      inputs.name ===
      (itemInfo.dir
        ? itemInfo.name
        : itemInfo.type
        ? itemInfo.name
            .split("")
            .reverse()
            .slice(
              itemInfo.name
                .split("")
                .reverse()
                .findIndex((item: string) => item === ".") + 1,
              itemInfo.name.split("").reverse().length
            )
            .reverse()
            .join("")
        : itemInfo.name)
    ) {
      setInputs((last) => ({ ...last, changed: false }));
    } else {
      setInputs((last) => ({ ...last, changed: true }));
    }
  }, [inputs.name, itemInfo.dir, itemInfo.name, itemInfo.type]);

  useEffect(() => {
    if (renameData.actionStatus === "success" && inputs.changed) {
      dispatch(resetRenameStatusAction());

      setInputs({
        name: itemInfo.dir
          ? itemInfo.name
          : itemInfo.type
          ? itemInfo.name
              .split("")
              .reverse()
              .slice(
                itemInfo.name
                  .split("")
                  .reverse()
                  .findIndex((item: string) => item === ".") + 1,
                itemInfo.name.split("").reverse().length
              )
              .reverse()
              .join("")
          : itemInfo.name,
        type: itemInfo.type ? itemInfo.type : "",
        changed: false,
      });

      enqueueSnackbar("Changes saved successfully.", {
        variant: "success",
      });
    }
  }, [
    dispatch,
    enqueueSnackbar,
    inputs.changed,
    itemInfo.dir,
    itemInfo.name,
    itemInfo.type,
    renameData.actionStatus,
  ]);

  const setImageSrc = (item: { dir: boolean; type: string }) => {
    const checkImageExists = (name: string) => {
      try {
        require(`../../../../../../assets/megaDL-icones/${name}.png`);
        return true;
      } catch (error) {
        return false;
      }
    };

    if (item.dir === true) {
      return require(`../../../../../../assets/megaDL-icones/folder.png`);
    } else if (
      item.dir === false &&
      !!item.type &&
      checkImageExists(item.type)
    ) {
      return require(`../../../../../../assets/megaDL-icones/${item.type}.png`);
    } else {
      return require(`../../../../../../assets/megaDL-icones/none.png`);
    }
  };

  return (
    <Box className={classes.collapseBox}>
      <Box
        sx={{
          width: "100%",
          height: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "8px 0px 8px 0px",
        }}
      >
        <Box
          component="img"
          src={setImageSrc(itemInfo)}
          alt={itemInfo.name}
          className={classes.images}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          margin: "0px 0px 8px 0px",
        }}
      >
        <Typography
          sx={{
            width: "calc(100% - 16px)",
            height: "24px",
            padding: "0px 0px 8px 0px",
            lineHeight: "1",
            fontSize: "1rem",
            opacity: "0.5",
            [theme.breakpoints.down("xl")]: {
              fontSize: "14px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "1rem",
            },
          }}
        >
          Name
        </Typography>

        <TextField
          name="name"
          type="text"
          value={inputs.name}
          onBlur={() =>
            openDrawer &&
            !inputs.name &&
            enqueueSnackbar("Please complete the name field!", {
              variant: "error",
            })
          }
          onChange={(e) =>
            setInputs((last) => ({
              ...last,
              name: e.target.value,
            }))
          }
          size={"small"}
          sx={{
            label: { color: (theme) => theme.palette.text.secondary },
            input: {
              padding: "10px 8px",
              fontSize: "14px",
              color: (theme) => theme.palette.text.secondary,
              "&::placeholder": {
                fontSize: "14px",
                color: (theme) => theme.palette.text.secondary,
              },
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                transition: " background-color 600000s 0s, color 600000s 0s",
              },
              [theme.breakpoints.down("xl")]: {
                fontSize: "12px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "14px",
              },
            },
            width: "100%",
          }}
          className={classes.field}
        />
      </Box>

      {itemInfo.dir === false && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            margin: "0px 0px 8px 0px",
          }}
        >
          <Typography
            sx={{
              width: "calc(100% - 16px)",
              height: "24px",
              padding: "0px 0px 8px 0px",
              lineHeight: "1",
              fontSize: "1rem",
              opacity: "0.5",
              [theme.breakpoints.down("xl")]: {
                fontSize: "14px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "1rem",
              },
            }}
          >
            Type
          </Typography>

          <Typography
            sx={{
              width: "calc(100% - 16px)",
              height: "24px",
              padding: "0px 0px 8px 0px",
              lineHeight: "1",
              fontSize: "15px",
              [theme.breakpoints.down("xl")]: {
                fontSize: "13px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "15px",
              },
            }}
          >
            {!!itemInfo.type ? itemInfo.type : "unknown"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          margin: "0px 0px 8px 0px",
        }}
      >
        <Typography
          sx={{
            width: "calc(100% - 16px)",
            height: "24px",
            padding: "0px 0px 8px 0px",
            lineHeight: "1",
            fontSize: "1rem",
            opacity: "0.5",
            [theme.breakpoints.down("xl")]: {
              fontSize: "14px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "1rem",
            },
          }}
        >
          Size
        </Typography>

        <Typography
          sx={{
            width: "calc(100% - 16px)",
            height: "24px",
            padding: "0px 0px 8px 0px",
            lineHeight: "1",
            fontSize: "15px",
            [theme.breakpoints.down("xl")]: {
              fontSize: "13px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "15px",
            },
          }}
        >
          {+itemInfo.size ? `${byteToMegabyte(+itemInfo.size)} mb` : "0 kb"}
        </Typography>
      </Box>

      {itemInfo.dir === false && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            margin: "0px 0px 8px 0px",
          }}
        >
          <Typography
            sx={{
              width: "calc(100% - 16px)",
              height: "24px",
              padding: "0px 0px 8px 0px",
              lineHeight: "1",
              fontSize: "1rem",
              opacity: "0.5",
              [theme.breakpoints.down("xl")]: {
                fontSize: "14px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "1rem",
              },
            }}
          >
            Modify
          </Typography>

          <Typography
            sx={{
              width: "calc(100% - 16px)",
              height: "24px",
              padding: "0px 0px 8px 0px",
              lineHeight: "1",
              fontSize: "15px",
              [theme.breakpoints.down("xl")]: {
                fontSize: "13px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "15px",
              },
            }}
          >
            {!!itemInfo.modify ? itemInfo.modify : "0"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          margin: "0px 0px 8px 0px",
        }}
      >
        <Typography
          sx={{
            width: "calc(100% - 16px)",
            height: "24px",
            padding: "0px 0px 8px 0px",
            lineHeight: "1",
            fontSize: "1rem",
            opacity: "0.5",
            [theme.breakpoints.down("xl")]: {
              fontSize: "14px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "1rem",
            },
          }}
        >
          Address
        </Typography>

        <Box
          sx={{
            width: "calc(100% - 16px)",
            display: "flex",
            alignItems: "flex-start",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
          }}
          className={classes.addressBox}
        >
          <SvgIcon
            component={FolderHomeIcon}
            inheritViewBox
            sx={{
              width: "auto",
              height: "16px",
              mr: "4px",
              color: theme.palette.secondary.light,
              [theme.breakpoints.down("xl")]: {
                height: "14px",
              },
              [theme.breakpoints.down("sm")]: {
                height: "16px",
              },
            }}
          />

          <Typography
            sx={{
              width: "calc(100% - 16px)",
              height: "24px",
              padding: "0px 0px 8px 0px",
              lineHeight: "1",
              fontSize: "15px",
              [theme.breakpoints.down("xl")]: {
                fontSize: "13px",
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "15px",
              },
            }}
          >
            {`/${itemInfo.path}`}
          </Typography>
        </Box>
      </Box>

      {openButton && (
        <Box
          sx={{
            width: "100%",
            height: openButton ? "fit-content" : "0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: "8px",
          }}
        >
          <Fade in={openButton} timeout={500}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                color="tertiary"
                fullWidth
                type="submit"
                sx={{
                  width: "90px",
                  height: "40px",
                  fontSize: "18px",
                  borderRadius: "8px",
                  textTransform: "none",
                  lineHeight: "0px",
                  padding: "12px",
                  mr: "8px",
                  color: "inherit",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  [theme.breakpoints.down("md")]: {
                    width: "calc(50% - 4px)",
                  },
                }}
                onClick={() =>
                  setInputs({
                    name: "",
                    type: itemInfo.type ? itemInfo.type : "",
                    changed: false,
                  })
                }
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                fullWidth
                type="submit"
                sx={{
                  width: "90px",
                  height: "40px",
                  fontSize: "18px",
                  borderRadius: "8px",
                  textTransform: "none",
                  lineHeight: "0px",
                  padding: "12px",
                  [theme.breakpoints.down("md")]: {
                    width: "calc(50% - 4px)",
                  },
                }}
                onClick={() => {
                  if (itemInfo.dir) {
                    if (
                      !!items.find(
                        (item) =>
                          item.name === inputs.name &&
                          item.path
                            .split("/")
                            .filter(
                              (item2: string, index2: number) =>
                                index2 > 0 &&
                                index2 < item.path.split("/").length - 1
                            )
                            .join("/") === itemInfo.path
                      )
                    ) {
                      enqueueSnackbar(
                        "A folder with this name has already been registered!",
                        {
                          variant: "error",
                        }
                      );
                    } else {
                      dispatch(renameFolderAction(inputs));
                    }
                  } else {
                    const fullName = itemInfo.type
                      ? `${inputs.name}.${itemInfo.type}`
                      : inputs.name;

                    if (
                      !!items.find(
                        (item) =>
                          item.name === fullName &&
                          item.path
                            ?.split("/")
                            .filter((item: string, index: number) => index > 0)
                            .join("/") === itemInfo.path
                      )
                    ) {
                      enqueueSnackbar(
                        "A file with this name has already been registered!",
                        {
                          variant: "error",
                        }
                      );
                    } else {
                      dispatch(renameFileAction(inputs));
                    }
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Fade>
        </Box>
      )}
    </Box>
  );
};

export default DrawerInfo;
